import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { addDaysIso, formatDateLabel, todayIso } from "@/lib/dates";

interface DayNavigatorProps {
  /** Current date as YYYY-MM-DD (empty string = "All"). */
  day: string;
  onChange: (day: string) => void;
  /** Optional custom label shown between the arrows and the date picker. */
  label?: string;
  /** Show a Clear button when a day is selected (for optional filtering). */
  allowClear?: boolean;
}

/**
 * Previous/Next arrows + a formatted date label + a date picker, matching the
 * load scheduler's day navigation. Used by the portal pages.
 */
export function DayNavigator({
  day,
  onChange,
  label,
  allowClear = false,
}: DayNavigatorProps) {
  const { t } = useTranslation();
  const shift = (delta: number) => onChange(addDaysIso(day || todayIso(), delta));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        size="icon"
        onClick={() => shift(-1)}
        aria-label="Previous day"
        title="Previous day"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        onClick={() => shift(1)}
        aria-label="Next day"
        title="Next day"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <div className="min-w-0 text-lg font-semibold">
        {label ?? (day ? formatDateLabel(day) : t("common.all"))}
      </div>
      <Input
        type="date"
        value={day}
        onChange={(e) => onChange(e.target.value)}
        className="w-44"
        aria-label={t("nav.loadScheduler")}
      />
      {allowClear && day ? (
        <Button variant="ghost" size="sm" onClick={() => onChange("")}>
          {t("common.clear")}
        </Button>
      ) : null}
    </div>
  );
}
